"use client";

import { useSearchParams } from "next/navigation";
import { useMemo } from "react";

import type { FunPaymentStage } from "@/components/fun-deposit/fun-payment-state";
import type { XchainFunDepositAddressErrorCode } from "@/contexts/mutations/fun/fun-deposit-address";
import { isProduction } from "@/env";

/**
 * Fun deposit pane state override; off in Vercel production, inert without `?funState=`. Fun has
 * no sandbox, so the pane renders its real states over a labelled stand-in and never opens a
 * provider session.
 */

const OVERRIDE_PARAM = "funState";

export type FunDepositBlockedReason = "notConfigured" | "subaccountRequired" | "belowMinimum";

export interface FunDepositOverride {
  /** Render this stage's body without a provider session behind it. */
  stage?: FunPaymentStage;
  /** Force the blocked branch, whatever config, subaccount and amount actually say. */
  blocked?: FunDepositBlockedReason;
  /** Fail the address request with this code instead of sending it. */
  serverErrorCode?: XchainFunDepositAddressErrorCode;
}

const SHORTHANDS: Record<string, FunDepositOverride> = {
  // No injected state: the stand-in drives the real callbacks and reducer.
  interactive: {},
  preparing: { stage: "preparing" },
  "checkout-open": { stage: "checkout-open" },
  closed: { stage: "closed" },
  "provider-done": { stage: "provider-done" },
  "could-not-start": { stage: "could-not-start" },
  blocked: { stage: "blocked" },
  notConfigured: { blocked: "notConfigured" },
  subaccountRequired: { blocked: "subaccountRequired" },
  belowMinimum: { blocked: "belowMinimum" },
  unsupportedAsset: { serverErrorCode: "unsupportedAsset" },
  chainUnavailable: { serverErrorCode: "chainUnavailable" },
  funUnavailable: { serverErrorCode: "funUnavailable" },
  invalidDepositAddress: { serverErrorCode: "invalidDepositAddress" },
};

function decode(raw: string | null): FunDepositOverride | null {
  if (!raw) return null;
  if (raw in SHORTHANDS) return SHORTHANDS[raw] ?? null;
  try {
    const json = typeof atob === "function" ? atob(raw) : Buffer.from(raw, "base64").toString();
    return JSON.parse(json) as FunDepositOverride;
  } catch {
    return null;
  }
}

export function useFunDepositOverride(): FunDepositOverride | null {
  const searchParams = useSearchParams();
  const urlValue = searchParams.get(OVERRIDE_PARAM);

  return useMemo(() => {
    if (isProduction) return null;
    return decode(urlValue);
  }, [urlValue]);
}
